import { useSyncExternalStore } from "react";

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  /** Milliseconds before auto-dismiss. Errors default to sticky. */
  duration: number;
}

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  duration?: number;
}

/*
 * A tiny external store so `toast()` can be called from anywhere (mutation
 * callbacks, loaders) without a React context. <Toaster/> subscribes to it.
 */
let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
let counter = 0;

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ToastItem[] {
  return toasts;
}

function show(input: ToastInput): string {
  const tone = input.tone ?? "info";
  const id = `toast-${++counter}`;
  const item: ToastItem = {
    id,
    title: input.title,
    description: input.description,
    tone,
    duration: input.duration ?? (tone === "danger" ? Number.POSITIVE_INFINITY : 5000),
  };
  toasts = [...toasts, item];
  emit();
  return id;
}

export function dismissToast(id: string): void {
  if (!toasts.some((item) => item.id === id)) return;
  toasts = toasts.filter((item) => item.id !== id);
  emit();
}

export function clearToasts(): void {
  toasts = [];
  emit();
}

export const toast = Object.assign(show, {
  success: (title: string, description?: string) => show({ title, description, tone: "success" }),
  error: (title: string, description?: string) => show({ title, description, tone: "danger" }),
  warning: (title: string, description?: string) => show({ title, description, tone: "warning" }),
  info: (title: string, description?: string) => show({ title, description, tone: "info" }),
});

export function useToast() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { toasts: items, toast, dismiss: dismissToast };
}
